const _ = require("lodash");
const WordUtil = require("./WordUtil");
const {V2} = require("./Vector");

const CIRCLE = 1;
const REGULAR_POLYGON = 2;
const NESTED_TRIANGLES = 3;
const JAGGED_SQUARE = 4;
const SQUARES_4SLICE = 5;
const VERTICAL_SLICE_SQUARE = 6;
const STAR = 7;
const HEXAGONAL_LATTICE = 8;
const RECTANGULAR_LATTICE = 9;
const HORIZONTAL_SLICE_SQUARE = 10;
const REGULAR_POLYGON_2SLICE = 11;
const SQUIGGLE_RECTANGLE = 12;
const RECTANGLE_TILES = 13;
const POLYGONS_REFLECT = 14;
const TILES = 15;

const diagram = function(text) {
	return "\\begin{diagram}\n" + text + "\\end{diagram}\n";
};

const isBoundedBy = function(p1, rect) {
	return _.every(p1, (x, i) => x <= rect[i] && x >= 0);
};

const vectorToAngle = function(vector) {
	return Math.atan2(vector[1], vector[0]);
};

const translate = function(pts, v, scale = 1) {
	return _.map(pts, pt => [pt[0] + v[0] * scale, pt[1] + v[1] * scale]);
};

// reflect p over point q
const reflectPoint = function(p, q) {
	const v1 = V2.sub(q, p);

	return V2.sum(p, v1, v1);
};

// reflect p over line made by a and b
const reflectLine = function(p, a, b) {
	const dx = b[0] - a[0];
	const dy = b[1] - a[1];

	const c = (dx * dx - dy * dy) / (dx * dx + dy * dy);
	const d = (2 * dx * dy) / (dx * dx + dy * dy);

	const x2 = c * (p[0] - a[0]) + d * (p[1] - a[1]) + a[0];
	const y2 = d * (p[0] - a[0]) - c * (p[1] - a[1]) + a[1];

	return [x2, y2];
};

const getAngleType = function(mAngle) {
	const diff = mAngle - Math.PI / 2;
	let type;

	if (diff < -0.001) {
		type = 0;
	} else if (diff > 0.001) {
		type = 2;
	} else {
		type = 1;
	}

	return type;
};

const measureAngle = function(p1, p2, p3) {
	const v1 = V2.sub(p1, p2).unit();
	const v2 = V2.sub(p3, p2).unit();

	return Math.acos(V2.dot(v1, v2));
};

const projectionToSegment = function(p, a, b) {
	return V2.sum(a, V2.project(V2.sub(p, a), V2.sub(b, a)));
};

const scaleForRect = function(w, h) {
	const size = Math.max(h, w / 2.5);
	let scale = 170 / size;
	scale = Math.min(scale, 30);

	return scale;
};

const genDrawRect = function(w, h, scale, offset, params) {
	const minDim = 8;
	let width = Math.ceil(w * scale);
	let height = Math.ceil(h * scale);
	width = Math.max(width, minDim);
	height = Math.max(height, minDim);

	let text =
		"rrDraw.rect(ctx," +
		offset.join(",") +
		"," +
		width +
		"," +
		height +
		", {\n";

	const hasFill = !!params.fillColor;
	if (hasFill) {
		text += "fillStyle : colors." + params.fillColor + ",\n";
	}
	const hasLineWidth = !!params.outerLineWidth;
	if (hasLineWidth) {
		text += "outerLineWidth : " + params.outerLineWidth + ",\n";
	}

	text += "hasFill : " + (hasFill ? "true" : "false") + ",\n";
	text += "hasGrid : true,\n";

	if (params.showUnitSquares) {
		text += "gridStyle : colors.darkgray,\n";
		text += "nGridH : " + (h - 1) + ",\n";
		text += "nGridV : " + (w - 1) + ",\n";
	}

	if (params.sideLabels) {
		text +=
			'sides : ["' +
			params.sideLabels[0] +
			'", "","","' +
			params.sideLabels[1] +
			'"],\n';
	} else {
		const unit = params.unit ? " " + params.unit : "";

		const wSide =
			params.showSides || params.showWidth ? '"' + w + unit + '"' : '""';
		const hSide =
			params.showSides || params.showHeight ? '"' + h + unit + '"' : '""';

		text += "sides : [" + wSide + ', "","",' + hSide + "],\n";
	}

	if (params.centeredText) {
		text += 'centeredText : "' + params.centeredText + '"\n';
	}

	text += "});\n";

	return text;
};

const genDrawAllRects = function(
	rectColumns,
	outerPadding,
	innerPadding,
	color,
	showOnlyLeftHeight
) {
	const numColumns = rectColumns.length;
	const columnWidths = rectColumns.map(c => _.max(c.map(rect => rect[0])));
	const columnHeights = rectColumns.map(c => _.sum(c.map(rect => rect[1])));
	const maxColumnLength = _.max(rectColumns.map(c => c.length));

	const scaleW = _.sum(columnWidths);
	const scaleH = _.max(columnHeights);

	const scale = scaleForRect(scaleW, scaleH);

	const fullW = scale * scaleW + (numColumns + 1) * outerPadding;
	const fullH =
		scale * scaleH + 2 * outerPadding + (maxColumnLength - 1) * innerPadding;

	let text = "ctx.setSize(" + [fullW, fullH].join(",") + ");\n";

	let xOffset = outerPadding;

	_.each(rectColumns, (column, i) => {
		let bottomY = fullH - outerPadding;

		const showHeight = !showOnlyLeftHeight || i === 0;

		_.each(column, (rect, j) => {
			text += genDrawRect(
				rect[0],
				rect[1],
				scale,
				[xOffset, bottomY - rect[1] * scale],
				{fillColor: color, showWidth: true, showHeight: showHeight}
			);
			bottomY -= rect[1] * scale + innerPadding;
		});

		xOffset += columnWidths[i] * scale + innerPadding;
	});

	return diagram(text);
};

const getSegmentDictionary = function(segmentInfos) {
	const segmentDict = {};

	_.each(segmentInfos, segmentInfo => {
		const pointKeys = segmentInfo.segment.map(x =>
			x.map(y => Math.round(y * 1000)).join("_")
		);
		pointKeys.sort();
		const key = pointKeys.join(":");

		if (!segmentDict[key]) {
			segmentDict[key] = {
				segment: segmentInfo.segment,
				pieces: [],
			};
		}

		segmentDict[key].pieces.push(segmentInfo.piece);
	});

	return segmentDict;
};

const polygonsToSegments = function(polygons) {
	const segments = [];
	_.each(polygons, (points, polyNum) => {
		_.each(_.range(points.length), i => {
			const p1 = points[i];
			const p2 = points[(i + 1) % points.length];
			segments.push({segment: [p1, p2], piece: polyNum});
		});
	});

	return segments;
};

const pointsToPolygonText = function(points) {
	let polyText = "";
	polyText += "ctx.moveTo(" + points[points.length - 1].join(",") + ");\n";
	_.each(points, pt => {
		polyText += "ctx.lineTo(" + pt.join(",") + ");\n";
	});
	return polyText;
};

const getRegPolyInfo = function(numSides, center, radius, useHalfAngleOffset) {
	if (!center) center = [0, 0];
	if (!radius) radius = 1;
	const offset = useHalfAngleOffset ? Math.PI / numSides : 0;
	const angles = _.range(numSides).map(
		i => (2 * Math.PI * i) / numSides - Math.PI / 2 + offset
	);
	const vectors = angles.map(x => [radius * Math.cos(x), radius * Math.sin(x)]);
	let endpoints = vectors.map(x => V2.sum(center, x));
	endpoints = endpoints.map(x => [x[0], x[1]]);

	return {
		angles: angles,
		endpoints: endpoints,
	};
};

const getBounds = function(points) {
	const minX = _.min(_.map(points, x => x[0]));
	const minY = _.min(_.map(points, x => x[1]));
	const maxX = _.max(_.map(points, x => x[0]));
	const maxY = _.max(_.map(points, x => x[1]));

	const center = [(maxX + minX) / 2, (maxY + minY) / 2];

	return {
		minX,
		maxX,
		minY,
		maxY,
		center,
	};
};

const getCenterTransformInfo = function(allPoints, scale, margin, theta) {
	if (!theta) theta = 0;

	const center = getBounds(allPoints).center;

	const rotate = pt => [
		Math.cos(theta) * pt[0] - Math.sin(theta) * pt[1],
		Math.sin(theta) * pt[0] + Math.cos(theta) * pt[1],
	];
	const translateThenRotate = pt =>
		rotate([pt[0] - center[0], pt[1] - center[1]]);

	const rotatedPoints = _.map(allPoints, pt => translateThenRotate(pt));

	const {minX, maxX, minY, maxY} = getBounds(rotatedPoints);

	scale = (scale * 120) / (maxY - minY);

	const transform = function(pt) {
		const pt2 = translateThenRotate(pt);
		return [(pt2[0] - minX) * scale + margin, (pt2[1] - minY) * scale + margin];
	};

	const size = [
		2 * margin + (maxX - minX) * scale,
		2 * margin + (maxY - minY) * scale,
	];

	return {
		points: _.map(allPoints, x => transform(x)),
		size,
		transform,
		scale,
	};
};

const shapeInfoForTemplate = function(template, scale) {
	const shapeType = template.shapeType;

	let segments = [];

	let arcs = [];
	let polygons = [];

	const margin = 10;

	const isStar = shapeType === STAR;
	const isRegPoly =
		shapeType === REGULAR_POLYGON || shapeType === REGULAR_POLYGON_2SLICE;
	const isCircle = shapeType === CIRCLE;

	const getCircleVector = function(radius, angle) {
		return [radius * Math.cos(angle), radius * Math.sin(angle)];
	};

	if (isCircle || isRegPoly || isStar) {
		const numPieces = template.numPieces;
		const center = [0, 0];
		const radius = 100;

		const numSides =
			shapeType === REGULAR_POLYGON_2SLICE ? numPieces / 2 : numPieces;

		const angles = _.range(numSides).map(i => (2 * Math.PI * i) / numSides);

		_.each(_.range(numSides), piece => {
			const pieceAngles = [piece, piece + 1].map(
				y => angles[y % angles.length] - Math.PI / 2
			);
			const vectors = pieceAngles.map(x => getCircleVector(radius, x));
			const endpoints = vectors.map(x => V2.sum(center, x));
			const arc = center.concat([radius]).concat(pieceAngles);

			if (isCircle) {
				arcs.push({arc: arc, piece: piece});
				segments.push({segment: [center, endpoints[0]], piece: piece});
				segments.push({segment: [center, endpoints[1]], piece: piece});
			} else if (isRegPoly) {
				if (shapeType === REGULAR_POLYGON_2SLICE) {
					const midpoint = _.map(
						[0, 1],
						x => (endpoints[0][x] + endpoints[1][x]) / 2
					);
					polygons.push([center, midpoint].concat([endpoints[0]]));
					polygons.push([center, midpoint].concat([endpoints[1]]));
				} else {
					polygons.push([center].concat(endpoints));
				}
			} else if (isStar) {
				const starScales = [0.4, 1];
				if (piece % 2 === 0) _.reverse(starScales);
				const vScaled = vectors.map((x, i) => x.map(y => y * starScales[i]));
				const starpoints = vScaled.map(x => V2.sum(center, x));
				polygons.push([center].concat(starpoints));
			}
		});
	} else {
		const reflectY = poly => _.map(poly, pt => [pt[0], -1 * pt[1]]);

		if (shapeType === TILES) {
			const dim = template.dim;
			let seedPolygons = template.polygons;
			seedPolygons = _.map(seedPolygons, reflectY);

			let vectors = template.vectors;
			vectors = reflectY(vectors);

			_.each(_.range(dim[0]), row => {
				_.each(_.range(dim[1]), col => {
					_.each(seedPolygons, poly => {
						const v1 = V2.scale(vectors[0], col);
						const v2 = V2.scale(vectors[1], row);

						polygons.push(translate(poly, V2.sum(v1, v2)));
					});
				});
			});
		} else if (shapeType === POLYGONS_REFLECT) {
			const reflectionFuncs = [];

			let lines = [];
			let points = [];

			if (template.line) lines.push(template.line);
			if (template.lines) lines = lines.concat(template.lines);

			if (template.point) points.push(template.point);
			if (template.points) points = points.concat(template.points);

			lines = _.map(lines, line => reflectY(line));
			points = reflectY(points);

			_.each(lines, line => {
				reflectionFuncs.push(x => reflectLine(x, line[0], line[1]));
			});

			_.each(points, pt => {
				reflectionFuncs.push(x => reflectPoint(x, pt));
			});

			let seedPolygons = template.polygons;
			seedPolygons = _.map(seedPolygons, reflectY);

			_.each(seedPolygons, poly => {
				polygons.push(poly);
			});

			_.each(reflectionFuncs, refFunc => {
				_.each(seedPolygons, poly => {
					polygons.push(_.map(poly, refFunc));
				});
			});
		} else if (shapeType === RECTANGLE_TILES) {
			const numRows = template.numRows;
			const numCols = template.numCols;
			const colPercent = template.colPercent || 0.5;
			const tilesPerCol = template.tilesPerCol || 2;

			const dr = 1 / numRows;
			const dc = colPercent / numCols;
			const cp = colPercent;

			_.each(_.range(numRows), x => {
				polygons.push(
					translate(
						[
							[0, 0],
							[1 - cp, 0],
							[1 - cp, dr],
							[0, dr],
						],
						[0, x * dr]
					)
				);
			});

			const colHeight = 1 / tilesPerCol;

			_.each(_.range(tilesPerCol), row => {
				_.each(_.range(numCols), x => {
					polygons.push(
						translate(
							[
								[0, 0],
								[dc, 0],
								[dc, colHeight],
								[0, colHeight],
							],
							[1 - cp + x * dc, row * colHeight]
						)
					);
				});
			});
		} else if (shapeType === RECTANGULAR_LATTICE) {
			const aspectRatio = template.aspectRatio || 1;
			let rows = template.rows;

			if (!rows) {
				const numCols = template.numCols;
				const numRows = template.numRows;

				rows = _.map(_.range(numRows), r => _.map(_.range(numCols), c => 1));
			}

			const unitY = 100;
			const unitX = aspectRatio * unitY;

			_.each(rows, (row, i) => {
				_.each(row, (value, j) => {
					if (value > 0) {
						const xOff = unitX * j;
						const yOff = unitY * i;
						const polygon = [
							[xOff, yOff],
							[xOff + unitX, yOff],
							[xOff + unitX, yOff + unitY],
							[xOff, yOff + unitY],
						];
						polygons.push(polygon);
					}
				});
			});
		} else if (shapeType === HEXAGONAL_LATTICE) {
			const cols = template.cols;
			const radius = 100;
			const maxCol = _.max(cols);
			const unitY = (radius * Math.sqrt(3)) / 2;
			const halfY = maxCol * unitY;

			_.each(cols, (numRows, col) => {
				const offsetX = col % 2 === 0 ? (3 * col) / 2 + 1 : (3 * col + 2) / 2;
				_.each(_.range(numRows), row => {
					const offsetY =
						numRows % 2 === 0
							? 2 * (row - numRows / 2) + 1
							: 2 * (row - (numRows - 1) / 2);
					const center = [offsetX * radius, halfY + offsetY * unitY];
					const rPolyInfo = getRegPolyInfo(6, center, radius, true);
					polygons.push(rPolyInfo.endpoints);
				});
			});
		} else if (
			shapeType === VERTICAL_SLICE_SQUARE ||
			shapeType === HORIZONTAL_SLICE_SQUARE
		) {
			const numPieces = template.numPieces;
			const isVertical = shapeType === VERTICAL_SLICE_SQUARE;
			const len = 150;

			const w = len / numPieces;

			_.each(_.range(numPieces), n => {
				let polygon = [
					[n * w, 0],
					[(n + 1) * w, 0],
					[(n + 1) * w, len],
					[n * w, len],
				];

				if (!isVertical) {
					polygon = _.map(polygon, x => [x[1], x[0]]);
				}

				polygons.push(polygon);
			});
		} else if (shapeType === SQUARES_4SLICE) {
			const len = 50;

			// const seeds = [[0, 0, [0, 3]], [1, 0, []], [2, 0, [0, 1]],
			//                 [0, 1, []], [1, 1, []], [2, 1, []],
			//                 [0, 2, [2, 3]], [1, 2, []], [2, 2, [1, 2]]];

			const squares = template.squares;

			squares.map(seed => {
				const x0 = seed[0] * len + len / 2;
				const y0 = seed[1] * len + len / 2;
				const types = _.difference([3, 0, 1, 2], seed[2]); // 3, 0, 1, 2 for correct shading ordering
				_.each(types, type => {
					const v0 = [len / 2, len / 2];
					const v1 = [len / 2, len / 2];
					if (type === 0 || type === 3) v0[0] = -1 * v0[0];
					if (type === 0 || type === 1) v0[1] = -1 * v0[1];
					if (type === 2 || type === 3) v1[0] = -1 * v1[0];
					if (type === 0 || type === 3) v1[1] = -1 * v1[1];

					const points = [
						[x0, y0],
						[x0 + v0[0], y0 + v0[1]],
						[x0 + v1[0], y0 + v1[1]],
					];

					polygons.push(points);
				});
			});
		} else if (shapeType === JAGGED_SQUARE) {
			const len = 50;

			const seeds = [
				[1, 1, [2, 3]],
				[2, 1, [5]],
				[2, 1.5, [1, 2, 3]],
				[1, 2, [0, 7]],
			];

			seeds.map(seed => {
				const x0 = seed[0] * len;
				const y0 = seed[1] * len;
				return _.each(seed[2], type => {
					const sides = type < 4 ? [len, len / 2] : [len / 2, len];
					type = type % 4;
					if (type === 1 || type === 2) sides[0] = -1 * sides[0];
					if (type === 0 || type === 1) sides[1] = -1 * sides[1];

					const points = [
						[x0, y0],
						[x0 + sides[0], y0],
						[x0, y0 + sides[1]],
					];

					polygons.push(points);
				});
			});
		} else if (shapeType === NESTED_TRIANGLES) {
			const numRows = template.numRows;

			let coords = _.range(numRows).map(x => {
				const nextCoords = [];
				const baseRange = _.range(2 * (x + 1) - 1);

				// to get the desired traversal,
				// _.each(_.filter(baseRange, y => y % 2 === 1), y => nextCoords.push([x, y]));
				// _.each(_.filter(baseRange, y => y % 2 === 0), y => nextCoords.push([x, y]));

				_.each(baseRange, y => nextCoords.push([x, y]));

				return nextCoords;
			});
			coords = _.flatten(coords);
			_.reverse(coords);

			const len = 160 / numRows;

			_.each(coords, piece => {
				const row = piece[0];
				const col = piece[1];
				const ys = [row, row + 1].map(x => (x * len * Math.sqrt(3)) / 2);
				const x0 = (len * (numRows - 1 - row + col)) / 2;

				let points;

				if (col % 2 === 0) {
					points = [
						[x0, ys[1]],
						[x0 + len / 2, ys[0]],
						[x0 + len, ys[1]],
					];
				} else {
					points = [
						[x0, ys[0]],
						[x0 + len / 2, ys[1]],
						[x0 + len, ys[0]],
					];
				}

				polygons.push(points);
			});
		}
	}

	const allPoints = [];

	_.each(polygons, polygon => {
		_.each(polygon, pt => {
			allPoints.push(pt);
		});
	});

	_.each(segments, seg => {
		_.each(seg.segment, pt => {
			allPoints.push(pt);
		});
	});

	_.each(arcs, arcInfo => {
		let arc = arcInfo.arc;

		arc = [].concat(arc);
		while (arc[4] < arc[3]) arc[4] += 2 * Math.PI;

		const center = arc.slice(0, 2);
		const radius = arc[2];
		const angleDelta = arc[4] - arc[3];
		const numAngles = 20;
		const angles = _.map(
			_.range(numAngles),
			x => arc[3] + (angleDelta * x) / (numAngles - 1)
		);

		const vectors = _.map(angles, x => getCircleVector(radius, x));
		const endpoints = vectors.map(x => V2.sum(center, x));
		_.each(endpoints, pt => {
			allPoints.push(pt);
		});
	});

	const transformInfo = getCenterTransformInfo(
		allPoints,
		scale,
		margin,
		template.rotation
	);
	const mapPoint = transformInfo.transform;
	const size = transformInfo.size;
	scale = transformInfo.scale;

	polygons = _.map(polygons, polygon => {
		return _.map(polygon, pt => {
			return mapPoint(pt);
		});
	});

	segments = _.map(segments, segInfo => {
		return {
			segment: _.map(segInfo.segment, pt => {
				return mapPoint(pt);
			}),
			piece: segInfo.piece,
		};
	});

	arcs = _.map(arcs, arcInfo => {
		const arc = arcInfo.arc;
		let newCenter = arc.slice(0, 2);
		newCenter = mapPoint(newCenter);
		const newRadius = scale * arc[2];

		return {
			arc: newCenter.concat([newRadius]).concat(arc.slice(3, 5)),
			piece: arcInfo.piece,
		};
	});

	let polygonsText = [];

	_.each(arcs, arcInfo => {
		const arc = arcInfo.arc;
		const center = arc.slice(0, 2);
		let pieceText = "";
		pieceText += "ctx.moveTo(" + center.join(",") + ");\n";
		pieceText += "ctx.arc(" + arc.join(",") + ");\n";
		pieceText += "ctx.moveTo(" + center.join(",") + ");\n";
		polygonsText.push(pieceText);
	});

	polygonsText = polygonsText.concat(polygons.map(x => pointsToPolygonText(x)));
	segments = segments.concat(polygonsToSegments(polygons));

	segments = getSegmentDictionary(segments);

	return {
		polygonsText: polygonsText,
		segments: segments,
		arcs: arcs,
		size: size,
	};
};

const groupStrokeElements = function(shapeInfo, outlineGroups) {
	const segmentDictionary = shapeInfo.segments;
	const arcInfos = shapeInfo.arcs;

	const thick = {
		arcs: [],
		segments: [],
	};
	let thin = {
		arcs: [],
		segments: [],
	};

	if (!outlineGroups) {
		thin = {
			segments: _.map(segmentDictionary, x => x.segment),
			arcs: _.map(arcInfos, x => x.arc),
		};
	} else {
		const updateElementArray = (name, data, pieces) => {
			const numInByGroup = _.map(outlineGroups, group => {
				let numIn = 0;

				_.each(pieces, piece => {
					if (_.includes(group, piece)) numIn++;
				});

				return numIn;
			});

			if (_.includes(numInByGroup, 1)) {
				thick[name].push(data);
			} else {
				thin[name].push(data);
			}
		};

		_.each(segmentDictionary, segmentInfo => {
			updateElementArray("segments", segmentInfo.segment, segmentInfo.pieces);
		});

		_.each(arcInfos, arcInfo => {
			updateElementArray("arcs", arcInfo.arc, [arcInfo.piece]);
		});
	}

	return {
		thick: thick,
		thin: thin,
	};
};

const drawStrokeElements = function(elements, strokeWidth) {
	let text = "";

	text += "ctx.strokeStyle = 'black';\n";
	text += "ctx.lineWidth = " + strokeWidth + ";\n";

	_.each(elements.arcs, arc => {
		text += "ctx.beginPath();\n";
		text += "ctx.arc(" + arc.join(",") + ");\n";
		text += "ctx.stroke();\n";
	});

	text += "ctx.beginPath();\n";

	_.each(elements.segments, seg => {
		text += "ctx.moveTo(" + seg[0].join(",") + ");\n";
		text += "ctx.lineTo(" + seg[1].join(",") + ");\n";
	});

	text += "ctx.stroke();\n";
	text += "ctx.closePath();\n";

	return text;
};

const getNameForTemplate = function(template) {
	let name = "shape";

	const shapeType = template.shapeType;

	if (shapeType === STAR) {
		name = "star";
	} else if (shapeType === REGULAR_POLYGON) {
		name = WordUtil.regularPolygon(template.numPieces);
	} else if (shapeType === REGULAR_POLYGON_2SLICE) {
		name = WordUtil.regularPolygon(template.numPieces / 2);
	} else if (shapeType === CIRCLE) {
		name = "circle";
	} else if (
		shapeType === VERTICAL_SLICE_SQUARE ||
		shapeType === HORIZONTAL_SLICE_SQUARE
	) {
		name = "square";
	} else if (shapeType === NESTED_TRIANGLES) {
		name = "triangle";
	}

	return name;
};

const genDrawTemplateShape = function(template, scale) {
	if (!scale) scale = 1;
	const shapeInfo = shapeInfoForTemplate(template, scale);
	const size = shapeInfo.size;

	let shadedGroups;
	let colors = template.color;

	if (!Array.isArray(colors)) colors = [colors];

	const numPieces = shapeInfo.polygonsText.length;

	if (template.shaded) {
		shadedGroups = [template.shaded];
	} else {
		let shadeStart = template.shadeStart || 0;

		const numShaded = template.numShaded || numPieces;

		if (Array.isArray(numShaded)) {
			shadedGroups = [];

			_.each(numShaded, x => {
				const group = _.range(shadeStart, shadeStart + x);
				shadedGroups.push(group);
				shadeStart += x;
			});
		} else {
			shadedGroups = [_.range(shadeStart, shadeStart + numShaded)];
		}

		shadedGroups = _.map(shadedGroups, group =>
			_.map(group, x => x % numPieces)
		);
	}

	const polygonsText = shapeInfo.polygonsText;

	const strokeElements = groupStrokeElements(shapeInfo, template.outlineGroups);

	let text = "";
	text += "ctx.setSize(" + size.join(",") + ")\n";

	const center = _.map(size, x => x / 2);
	const encircleArc = `ctx.arc(${center
		.concat([size[1] * 0.38])
		.join(",")}, 0, ${2 * Math.PI});`;

	if (template.encircle) {
		text += `
ctx.save();
${encircleArc}
ctx.clip();
`;
	}

	_.each(shadedGroups, (shadedGroup, i) => {
		text += "ctx.beginPath();\n";

		_.each(shadedGroup, polyIdx => {
			text += polygonsText[polyIdx];
		});

		text += "ctx.fillStyle = colors." + colors[i] + ";\n";
		text += "ctx.fill();\n";
		text += "ctx.closePath();\n";
	});

	text += drawStrokeElements(strokeElements.thin, 1);

	text += drawStrokeElements(strokeElements.thick, 3);

	if (template.encircle) {
		text += `
ctx.restore();
ctx.beginPath();
${encircleArc}
ctx.stroke();
ctx.closePath();
`;
	}

	text = diagram(text);

	let name = template.encircle ? "circle" : template.name;
	if (!name) name = getNameForTemplate(template);

	return _.extend({
		text: text,
		name: name,
		numPieces: numPieces,
	});
};

const genDrawToothpicks = function(toothpicks, margin, scale) {
	if (!scale) scale = 40;

	const maxX = _.max(toothpicks.map(x => Math.max(x[0], x[2])));
	const maxY = _.max(toothpicks.map(x => Math.max(x[1], x[3])));
	const dim = [maxX, maxY];

	const size = dim.map((x, i) => x * scale + 2 * margin[i]);

	let text = "";
	text += "ctx.setSize(" + size.join(",") + ")\n";
	text += toothpicks
		.map(tp => {
			return (
				"drawToothpick(ctx," +
				tp.map((x, i) => x * scale + margin[i % 2]).join(",") +
				");\n"
			);
		})
		.join("");
	text = diagram(text);

	return text;
};

// labelMode: (0 = single side, 1 = all sides, 2 = single side + empty blanks, 3 = all blanks completed, 4 = bottom side)

const genDrawPoly = function(sides, length, color, labelMode, flatBottom) {
	const usingBlanks = _.includes([2, 3], labelMode);
	const revealSingleSide = _.includes([0, 2, 4], labelMode);
	const unit = 15;
	const border = usingBlanks ? 35 : 25;

	const center = "[5,5]";

	const rotation = flatBottom && sides % 2 === 0 ? ", 180/" + sides : "";

	let text = "";
	text += "var unit = " + unit + ";\n";
	text += "var border = " + border + ";\n";
	text += "ctx.setSize(unit*10+2*border, 10*unit+2*border);\n";
	text +=
		"var area = ShapeArea(ctx, border, border, unit*10, unit*10, 10, 10);\n";
	text +=
		"var coords = area.generateRegularCoordinates(" +
		sides +
		", " +
		center +
		", 5" +
		rotation +
		");\n";
	text += "area.shape(coords, {fill: colors." + color + "});\n";

	let defaultLabelStr = revealSingleSide ? "" : length + "";

	if (usingBlanks) {
		const blankStr =
			defaultLabelStr.length > 0 ? "[" + defaultLabelStr + "]" : "";
		defaultLabelStr = "$\\\\blank" + blankStr + "{2}$";
	}

	const sideToAlwaysLabel =
		labelMode === 4 ? Math.ceil(sides / 2) - 1 : sides - 1;
	_.range(sides).map(i => {
		const labelStr = i === sideToAlwaysLabel ? length + "" : defaultLabelStr;

		if (labelStr.length > 0) {
			text +=
				"area.labelOutside(coords[" +
				i +
				"], coords[" +
				((i + 1) % sides) +
				"], " +
				center +
				', "' +
				labelStr +
				'");\n';
		}
	});

	text = diagram(text);

	return text;
};

const getContextSize = function(w, h, useExtraPaddingX, extraScale) {
	if (!extraScale) extraScale = 1;
	const scale = scaleForRect(w, h) * extraScale;
	const padding = [useExtraPaddingX ? 80 : 40, 40];

	const ctxW = scale * w + 2 * padding[0];
	const ctxH = scale * h + 2 * padding[1];

	return {
		ctxSize: [ctxW, ctxH],
		scale: scale,
		padding: padding,
	};
};

const rectDiagramWithParams = function(
	w,
	h,
	params,
	useExtraPaddingX,
	extraScale
) {
	const sizeInfo = getContextSize(w, h, useExtraPaddingX, extraScale);
	let text = "";
	text += "ctx.setSize(" + sizeInfo.ctxSize.join(",") + ");\n";
	text += genDrawRect(w, h, sizeInfo.scale, sizeInfo.padding, params);
	text = diagram(text);

	return text;
};

const rightTrianglePermutations = {
	0: [0, 1, 2, 3],
	1: [1, 0, 3, 2],
	2: [2, 3, 0, 1],
	3: [3, 2, 1, 0],
	4: [2, 3, 0, 1],
	5: [0, 1, 2, 3],
};

const veryClose = function(a, b) {
	return Math.abs(a - b) <= 0.00001;
};

const pointsNeedInvert = function(p1, p2) {
	return p2[0] < p1[0] || (veryClose(p2[0], p1[0]) && p2[1] < p1[1]);
};

// permutations 5 and 6 align the hypotenuse to be level
const rightTriangleDiagram = function(
	width,
	height,
	permutation,
	unit,
	color,
	drawRectangle
) {
	const hypotenuse = Math.sqrt(width * width + height * height);

	let alignToHyp = false;
	let deltaX = 0;
	let deltaY = 0;
	let y0 = 0;

	let gridSize;

	if (permutation > 3) {
		alignToHyp = true;
		deltaX = (width * width) / hypotenuse;
		deltaY = (width * height) / hypotenuse;
		gridSize = [hypotenuse, Math.ceil((drawRectangle ? 2 : 1) * deltaY)];
		y0 = deltaY;
		if (permutation === 4 && !drawRectangle) y0 = 0;
	} else {
		gridSize = [width, height];
	}

	const sizeInfo = getContextSize(...gridSize, false);

	const scale = sizeInfo.scale;
	const perm = rightTrianglePermutations[permutation];

	const scaledSize = gridSize.map(x => x * scale);

	let text = "";
	text += "ctx.setSize(" + sizeInfo.ctxSize.join(",") + ");\n";
	text +=
		"var area = ShapeArea(ctx, " +
		sizeInfo.padding
			.concat(scaledSize)
			.concat(gridSize)
			.join(",") +
		")\n";

	const pointNames = ["A", "B", "C", "D"];
	let points;

	if (alignToHyp) {
		points = [
			[0, y0],
			[deltaX, y0 - deltaY],
			[hypotenuse, y0],
			[hypotenuse - deltaX, y0 + deltaY],
		];
	} else {
		points = [
			[0, 0],
			[width, 0],
			[width, height],
			[0, height],
		];
	}

	_.each(_.range(4), i => {
		text += "var " + pointNames[i] + " = V(" + points[i].join(",") + ")\n";
	});

	const pts = perm.map(x => pointNames[x]);
	points = perm.map(x => points[x]);

	text +=
		"area.shape([" +
		pts.slice(0, 3).join(",") +
		"], {lineWidth: 1, fill: colors." +
		color +
		", rightAngles: true})\n";

	const lengths = [width, height, hypotenuse];
	const centroid = [0, 1].map(
		i => _.sum(points.slice(0, 3).map(x => x[i])) / 3
	);

	_.each(
		[
			[0, 1],
			[1, 2],
			[0, 2],
		],
		(ptIndexes, i) => {
			const length = lengths[i];
			if (length - Math.floor(length) === 0) {
				const p1 = points[ptIndexes[0]];
				const p2 = points[ptIndexes[1]];
				const needsInvert = pointsNeedInvert(p1, p2);
				if (needsInvert) _.reverse(ptIndexes);
				const proj = projectionToSegment(centroid, p1, p2);
				const isBelow =
					proj[1] < centroid[1] ||
					(veryClose(proj[1], centroid[1]) && proj[0] > centroid[0]);
				text +=
					"area.label(" +
					ptIndexes.map(x => pts[x]).join(",") +
					', "' +
					lengths[i] +
					" " +
					unit +
					'",{below:' +
					isBelow +
					"})\n";
			}
		}
	);

	if (drawRectangle) {
		text += "area.line(" + pts[2] + "," + pts[3] + ")\n";
		text += "area.line(" + pts[0] + "," + pts[3] + ")\n";
	}

	text = diagram(text);

	return text;
};

const basicRectDiagram = function(
	w,
	h,
	color,
	showUnitSquares,
	unit,
	useExtraPaddingX,
	extraScale,
	outerLineWidth
) {
	return rectDiagramWithParams(
		w,
		h,
		{
			fillColor: color,
			outerLineWidth: outerLineWidth,
			showUnitSquares: showUnitSquares,
			showSides: true,
			unit: unit,
		},
		useExtraPaddingX,
		extraScale
	);
};

const shapeTemplatesB = {
	0: {
		shapeType: 3,
		numRows: 4,
		numPieces: 16,
	},
	1: {
		shapeType: 4,
		numPieces: 8,
	},
	2: {
		shapeType: 5,
		numPieces: 12,
		squares: [
			[0, 0, []],
			[1, 0, []],
			[2, 0, []],
		],
	},
	3: {
		shapeType: 8,
		numPieces: 9,
		cols: [1, 2, 3, 2, 1],
	},
	4: {
		shapeType: 9,
		numPieces: 8,
		rows: [
			[0, 1, 1],
			[1, 1, 1],
			[1, 1, 1],
		],
	},
};

const builtTemplates = {};
_.each(_.range(1, 24), i => (builtTemplates[i] = []));

const addShapeTemplate = function(shapeType, numPieces, templateExtra) {
	if (!templateExtra) templateExtra = {};
	builtTemplates[numPieces].push(
		_.extend(
			{
				shapeType: shapeType,
				numPieces: numPieces,
			},
			templateExtra
		)
	);
};

const getTemplatesWithNumPieces = function(numPieces) {
	return builtTemplates[numPieces];
};

_.each([2, 3, 4], numRows => {
	addShapeTemplate(NESTED_TRIANGLES, numRows * numRows, {numRows: numRows});
});

_.each(_.range(2, 11), numPieces => {
	const shapeTypes = [1, 6, 10];
	if (numPieces % 2 === 0) {
		if (numPieces >= 8) {
			shapeTypes.push(STAR);
		}

		if (numPieces >= 6) {
			shapeTypes.push(REGULAR_POLYGON_2SLICE);
		}
	}

	if (numPieces > 2) {
		shapeTypes.push(REGULAR_POLYGON);
	}

	_.each(shapeTypes, shapeType => {
		addShapeTemplate(shapeType, numPieces);
	});
});

addShapeTemplate(JAGGED_SQUARE, 8);

_.each([1, 2, 3], numSquares => {
	addShapeTemplate(SQUARES_4SLICE, 4 * numSquares, {
		squares: _.map(_.range(numSquares), x => [x, 0, []]),
	});
});

const template8Cols = [];

_.each([[1, 2, 3, 2, 1]], baseCols => {
	_.each(_.range(2, baseCols.length + 1), numCols => {
		const cols = baseCols.slice(0, numCols);
		template8Cols.push(cols);
	});
});

template8Cols.push([2, 3, 2]);

_.each(template8Cols, cols => {
	addShapeTemplate(HEXAGONAL_LATTICE, _.sum(cols), {
		cols: cols,
	});
});

const template9Tops = [
	[0, 1, 1],
	[1, 1, 0],
	[0, 1, 0],
];
const template9Bottoms = [
	[0, 1, 1],
	[1, 1, 0],
	[0, 1, 0],
];

_.each(template9Tops, top => {
	_.each(template9Bottoms, bottom => {
		const rows = [top, [1, 1, 1], bottom];
		addShapeTemplate(RECTANGULAR_LATTICE, _.sum(_.map(rows, x => _.sum(x))), {
			rows: rows,
		});
	});
});

const getShapeTemplate = function(templateNum, randomGen) {
	const template = _.extend({}, shapeTemplatesB[templateNum]);
	template.shadeStart = 0;

	return template;
};

const drawPolyGeneral = function(
	givenPoints,
	{
		color = "black",
		avoidFill = false,
		squiggles = undefined,
		scale = 1,
		rotation = 0,
	} = {}
) {
	const transformInfo = getCenterTransformInfo(givenPoints, scale, 3);
	const size = transformInfo.size;

	const points = transformInfo.points;

	if (!squiggles) squiggles = _.map(points, x => 0);

	const firstPoint = points.splice(0, 1)[0];
	let lastPoint = firstPoint;

	let text = "";
	text += "ctx.setSize(" + size.join(",") + ")\n";
	text += "ctx.beginPath();\n";
	text += "ctx.moveTo(" + firstPoint.join(",") + ");\n";
	text +=
		_.map(points, (pt, i) => {
			let lineText;

			const squiggleScale = squiggles[i];

			if (squiggleScale !== 0) {
				const vec = V2.sub(pt, lastPoint);
				const bezier = _.map([0, 1], x => (lastPoint[x] + pt[x]) / 2);
				bezier[0] += -1 * squiggleScale * vec[1];
				bezier[1] += squiggleScale * vec[0];
				lineText =
					"ctx.quadraticCurveTo(" +
					bezier.join(",") +
					"," +
					pt.join(",") +
					");";
			} else {
				lineText = "ctx.lineTo(" + pt.join(",") + ");";
			}

			lastPoint = pt;

			return lineText;
		}).join("\n") + "\n";

	if (!avoidFill) {
		text += "ctx.closePath();\n";
		text += "ctx.fillStyle = colors." + color + ";\n";
		text += "ctx.fill();\n";
	}

	text += "ctx.lineWidth = 1;\n";
	text += "ctx.strokeStyle = 'black';\n";
	text += "ctx.stroke();\n";
	text = diagram(text);

	return text;
};

const drawShape = function(input, substitutions) {
	const shapeTemplate = {
		color: substitutions.color,
	};

	if (substitutions.shapeTemplate) {
		_.extend(shapeTemplate, substitutions.shapeTemplate);
	} else {
		shapeTemplate.shapeType = substitutions.shapeType || CIRCLE;

		if (substitutions.denominator) {
			const numPieces = substitutions.denominator;
			const numShaded = substitutions.numerator || numPieces;
			const shadedOffset = substitutions.shadedOffset || 0;

			shapeTemplate.numPieces = numPieces;
			shapeTemplate.shaded = _.map(
				_.range(shadedOffset, shadedOffset + numShaded),
				x => x % numPieces
			);
		} else {
			const shapeKeys = [
				"numPieces",
				"numRows",
				"numCols",
				"numShaded",
				"aspectRatio",
			];

			_.each(shapeKeys, key => {
				if (substitutions[key]) shapeTemplate[key] = substitutions[key];
			});
		}
	}

	return genDrawTemplateShape(shapeTemplate).text;
};

module.exports = {
	vectorToAngle,
	isBoundedBy,
	measureAngle,
	getAngleType,

	genDrawTemplateShape,
	getShapeTemplate,
	scaleForRect,
	basicRectDiagram,
	rectDiagramWithParams,
	rightTriangleDiagram,
	genDrawRect,
	genDrawAllRects,
	genDrawPoly,
	drawPolyGeneral,
	getRegPolyInfo,
	genDrawToothpicks,
	getTemplatesWithNumPieces,
	getNameForTemplate,

	drawShape,

	CIRCLE,
	REGULAR_POLYGON,
	NESTED_TRIANGLES,
	JAGGED_SQUARE,
	SQUARES_4SLICE,
	VERTICAL_SLICE_SQUARE,
	STAR,
	HEXAGONAL_LATTICE,
	RECTANGULAR_LATTICE,
	HORIZONTAL_SLICE_SQUARE,
	REGULAR_POLYGON_2SLICE,
	SQUIGGLE_RECTANGLE,
	RECTANGLE_TILES,
	POLYGONS_REFLECT,
	TILES,
};
